import React, { useEffect, useRef, useState } from "react";
import type { Match } from "../../../shared/types";

import * as scoreUtil from "core/src/core/score/score-util.js";
import { wormColors } from "core/src/core/constants.js";

import GameCanvas from "../../../game-canvas/GameCanvas";
import { parseEvent, CONTINUE_KEY, ENTER_KEY } from "../../../shared/key-util.js";
import Overlay from "../../../game-canvas/overlays/Overlay";
import DonateButton from "../../../shared/DonateButton";
import { useAuth } from "../../../shared/auth";
import musicController from "../../../audio/music-controller";
import { resolveDisplayName } from "../../../shared/profile";
import { fetchRating } from "../../../shared/rating/store";
import type { PlayerRating } from "../../../shared/rating/rating";
import RankInsignia from "../../../shared/rating/RankInsignia";
import { flagUrl, isFlagCode } from "../../../shared/flags";
import { insigniaForValue } from "../../../shared/rating/tiers";
import MatchStatsBox from "./MatchStatsBox";
import WinnerCelebration, { celebrationFor } from "./WinnerCelebration";
import WormHeadAvatar from "./WormHeadAvatar";
import YourRatingCard from "./YourRatingCard";
import styles from "../play.module.scss";

import "./MatchOver.scss";
import AdSlot from "../../../shared/ads/AdSlot";

// Per-player rating shown on the results screen. Today only the signed-in player's
// own rank is known client-side (matched by name). When the server starts
// declaring match results it can pass the full map here, including a `delta`
// (rating change this match) to render alongside each rated player.
export interface PlayerRatingInfo {
  rating?: PlayerRating; // raw OpenSkill state (own fetched rating)
  value?: number; // or a server-declared display value (rating_update)
  delta?: number; // rating change this match
}

interface Props {
  match: Match;
  onRoundClick: (roundIndex: number) => void;
  onExitAction: () => void;
  // Play again: a local rematch with the same config, or a fresh join of the
  // same online box. Omitted where there is nothing sensible to rejoin.
  onPlayAgain?: () => void;
  // Online: players who left before the end. They rank last whatever their
  // score (the rating counts a walk-out as finishing last), marked "left".
  leftIds?: string[];
  playerRatings?: Record<string, PlayerRatingInfo>;
  // Fun-stat aggregate (core aggregateMatchStats shape). Online: the match_over
  // payload. Local: omitted - derived from the real match object below.
  matchStats?: any;
  // The viewer's own seat ids (whose stats are switchable) + which one leads.
  statsPlayerIds?: string[];
  mainPlayerId?: string;
  // Play the win or loss sound as it opens (off when another screen already did).
  sound?: boolean;
  // The changing rooms (Play Now, Royale) show this screen too, over the game:
  // a way back to it, and the exit and play-again buttons named for the room.
  onBack?: () => void;
  backLabel?: string;
  exitLabel?: string;
  playAgainLabel?: string;
  kicker?: string;      // a line over the hero ("Royale #214", "Play Now · match 3")
  // The Royale: the standings are its placings, best first, each with a word
  // instead of points ("Winner", "Out in stage 2", "Still in"). A Royale still
  // on has players "still in" at the top, and no winner yet.
  placings?: { id: string; note: string; still?: boolean }[];
  heroSub?: string;     // the hero's line (default "wins the match")
}

const ordinalOf = (n: number) => n + (n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th");

export default function MatchOver({ match, onRoundClick, onExitAction, onPlayAgain, leftIds, playerRatings, matchStats, statsPlayerIds, mainPlayerId, sound = true, onBack, backLabel = "Back to the game", exitLabel = "Back to Lobby", playAgainLabel = "Play again", kicker, placings, heroSub }: Props) {
  // CONTINUE / ENTER takes the primary action: play again when offered, else
  // exit to the lobby. Listener registered once; reads the latest handlers
  // through refs.
  const onExitActionRef = useRef(onExitAction);
  onExitActionRef.current = onExitAction;
  const onPlayAgainRef = useRef(onPlayAgain);
  onPlayAgainRef.current = onPlayAgain;

  // Resolve a rank for as many players as we can. Prefer server-declared data
  // (playerRatings prop); otherwise fall back to the signed-in player's own rank,
  // matched to their worm by display name (local matches carry no account id).
  const { user } = useAuth();
  const heroAvatarRef = useRef<HTMLDivElement>(null);
  const [myRating, setMyRating] = useState<PlayerRatingInfo | null>(null);
  useEffect(() => {
    if (!user) {
      setMyRating(null);
      return;
    }
    let live = true;
    fetchRating(user.id).then((r) => {
      if (live) setMyRating(r ? { rating: r.rating } : null);
    });
    return () => {
      live = false;
    };
  }, [user?.id]);

  const ratingByPlayerId: Record<string, PlayerRatingInfo> = { ...(playerRatings || {}) };
  if (user && myRating) {
    const myName = resolveDisplayName(user);
    const me = match.matchConfig.players.find((p: any) => p.name === myName);
    if (me && !ratingByPlayerId[me.id]) ratingByPlayerId[me.id] = myRating;
  }

  // `reserveSpace`: keep this player's badge column at its usual width even when
  // there's no rating to show, so the standings list's score numbers stay lined
  // up instead of the row's flexible name stretching into the gap and shoving
  // the score out to the row's edge. The podium doesn't need this - each card is
  // its own independently centered column, not part of a shared aligned column.
  function renderRank(playerId: string, className?: string, reserveSpace?: boolean) {
    const info = ratingByPlayerId[playerId];
    if (!info) return reserveSpace ? <span className={"match-over-rank " + (className || "")} /> : null;
    // A crossed tier boundary gets called out ("PROMOTED"/"DEMOTED") so the
    // whole lobby's drama is visible in the list, not just your own.
    const crossed =
      typeof info.delta === "number" && typeof info.value === "number"
        ? insigniaForValue(info.value).tier.id !== insigniaForValue(info.value - info.delta).tier.id
          ? info.delta > 0 ? "up" : "down"
          : null
        : null;
    // Column order: [PROMOTED / DEMOTED] [delta] [badge], the callout first so
    // the delta and badge columns line up across every row of the list.
    return (
      <span className={"match-over-rank " + (className || "")}>
        {crossed ? (
          <span className={"match-over-tierchange " + crossed}>{crossed === "up" ? "PROMOTED" : "DEMOTED"}</span>
        ) : null}
        {typeof info.delta === "number" ? (
          <span className={"match-over-rank-delta " + (info.delta >= 0 ? "up" : "down")}>
            {info.delta >= 0 ? "+" : ""}
            {info.delta}
          </span>
        ) : null}
        <RankInsignia rating={info.rating ?? null} value={info.value} size={22} />
      </span>
    );
  }
  useEffect(() => {
    function onKeyUp(event: KeyboardEvent) {
      const newKey = parseEvent(event);
      if (newKey === CONTINUE_KEY || newKey === ENTER_KEY) {
        (onPlayAgainRef.current ?? onExitActionRef.current)();
      }
    }
    document.addEventListener("keyup", onKeyUp);
    return () => document.removeEventListener("keyup", onKeyUp);
  }, []);

  // Best first; anyone who left the match goes below everyone who stayed.
  const left = new Set(leftIds || []);
  const scoreList = scoreUtil.createSortedList(match.getCurrentScore());
  const sortedMatchScore = [...scoreList.filter((s: any) => !left.has(s.id)), ...scoreList.filter((s: any) => left.has(s.id))];
  const topScore = sortedMatchScore[0].score;
  const topPlayers = sortedMatchScore
    .filter((s: any) => s.score === topScore && !left.has(s.id))
    .map((s: any) => match.matchConfig.players.find((p: any) => p.id === s.id));
  let matchTied = topPlayers.length > 1;
  let matchWinner = topPlayers[0];
  if (placings && placings.length) {
    const first = match.matchConfig.players.find((p: any) => p.id === placings[0].id);
    if (first) { matchWinner = first; matchTied = false; }
  }
  // Win or loss for whoever is at this device: online our seats, local every human
  // (with several on one keyboard, someone here won).
  const youIds: string[] = statsPlayerIds || (mainPlayerId ? [mainPlayerId] : match.matchConfig.players.filter((p: any) => p.type === "human").map((p: any) => p.id));
  const youWon = placings ? !!placings[0] && !placings[0].still && youIds.includes(placings[0].id) : topPlayers.some((p: any) => p && youIds.includes(p.id));
  useEffect(() => {
    if (sound) musicController.play(youWon ? "match-win" : "match-lost");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let equalScoreCounter = 0;
  let prevScore = -1;
  let placementIndex = 0;
  let standings = sortedMatchScore.map((playerScore: any) => {
    const walkedOut = left.has(playerScore.id);
    // Leavers all share last place; the ones who stayed rank on score.
    if (walkedOut) {
      const player = match.matchConfig.players.find((p: any) => p.id === playerScore.id);
      return { player, placement: sortedMatchScore.length, score: playerScore.score, left: true };
    }
    if (prevScore === playerScore.score) {
      equalScoreCounter++;
    } else {
      equalScoreCounter = 0;
    }
    prevScore = playerScore.score;

    const placement = placementIndex + 1 - equalScoreCounter;
    placementIndex++;

    const player = match.matchConfig.players.find((p: any) => p.id === playerScore.id);
    return { player, placement, score: playerScore.score, left: false };
  });
  // The Royale's placings take the place of the score's order: those still in
  // share the top, then everyone out by their place.
  const placed = placings ? placings.map((pl, i) => ({ player: match.matchConfig.players.find((p: any) => p.id === pl.id), placement: pl.still ? 1 : i + 1, score: 0, left: false, note: pl.note, still: !!pl.still })).filter((s) => s.player) : null;
  if (placed) standings = placed as any;
  const stillOn = !!placed && placed.some((s) => s.still);

  // The two info boxes under the standings: "Your rating" (online, once the
  // server has declared this match's rating movement) and "Match stats" (core's
  // fun-stat aggregate - local matches compute it right here, online it arrives
  // in the match_over payload).
  const stats = matchStats ?? ((match as any).getMatchStats ? (match as any).getMatchStats() : null);
  const humanIds = match.matchConfig.players.filter((p: any) => p.type === "human").map((p: any) => p.id);
  const switchableIds = statsPlayerIds && statsPlayerIds.length ? statsPlayerIds : humanIds;
  const statsPlayers = switchableIds
    .map((id: string) => match.matchConfig.players.find((p: any) => p.id === id))
    .filter(Boolean)
    .map((p: any) => ({ id: p.id, name: p.name }));
  const statsMainId = mainPlayerId || switchableIds[0];
  const myMove = statsMainId ? ratingByPlayerId[statsMainId] : null;
  const myRatingCard =
    myMove && typeof myMove.value === "number" && typeof myMove.delta === "number" ? (
      <YourRatingCard value={myMove.value} delta={myMove.delta} />
    ) : null;

  // The winner's moment (design 21 on the results board): the hero in the
  // middle with the runners-up flanking it, their celebration behind, then
  // every player as a score bar, the rating card and the stats beside them.
  // 2nd and 3rd flank the winner only in a match of four or more; with fewer
  // players the winner stands alone (the runners-up are the next bars anyway).
  // A tie for first lists the tied names in the hero instead, so no flanks.
  const runnersUp = !matchTied && !placings && standings.length >= 4 ? standings.slice(1, 3) : [];
  const winnerScore = Math.max(1, standings[0] ? standings[0].score : 1);
  // Rows to tag YOU: the viewer's own seats online; a couch match marks none
  // (every human is "you" there).

  // A runner-up beside the winner: avatar, name and the place in words. Their
  // points and rating are in the bars below.
  function flank(entry: any) {
    const color = wormColors[entry.player.colorId];
    const metal = entry.placement === 2 ? "silver" : "bronze";
    return (
      <div key={entry.player.id} className={styles.flank}>{/* the head they play with, like the winner's */}
        <div className={styles.flankAvatar} style={{ borderColor: color }}>
          <WormHeadAvatar player={entry.player} size={40} bodyRadius={11.5} />
        </div>
        <span className={styles.flankName} style={{ color }}>
          {isFlagCode(entry.player.flag) ? <img className={styles.podiumFlag} src={flagUrl(entry.player.flag)} alt="" /> : null}
          {entry.player.name}
        </span>
        <span className={styles.flankPlace + " " + (metal === "silver" ? styles.flankSilver : styles.flankBronze)}>
          {entry.placement === 2 ? "Second place" : "Third place"}
        </span>
      </div>
    );
  }

  const hero = stillOn ? (
    <div className={styles.hero}>
      {kicker ? <div className={styles.heroKicker}>{kicker}</div> : null}
      <span className={styles.heroCrown} />
      <div className={styles.heroName}>{placed!.filter((s) => s.still).length} still in</div>
      {(() => {
        const me = placed!.find((s) => youIds.includes(s.player.id) && !s.still);
        return me ? <div className={styles.heroYou}>You: {ordinalOf(me.placement)} · {me.note}</div> : null;
      })()}
      <div className={styles.heroNote}>The Royale is still on. The results fill in when it ends.</div>
    </div>
  ) : (
    <div className={styles.hero}>
      {kicker ? <div className={styles.heroKicker}>{kicker}</div> : null}{/* the celebration is anchored to the avatar below */}
      {/* The celebration only for a winner who owns one; a plain winner gets a
          plain hero. No extra crown glyph: the celebration is the whole show.
          The empty slot keeps the hero's headroom the same either way. */}
      {celebrationFor(matchWinner) ? <WinnerCelebration player={matchWinner} anchorRef={heroAvatarRef} className={styles.heroFx} /> : null}
      {(() => {
        return <span className={styles.heroCrown} />;
      })()}
      {/* the head they actually play with, cosmetic included */}
      <div ref={heroAvatarRef} className={styles.heroAvatar} style={{ borderColor: wormColors[matchWinner.colorId] }}>
        <WormHeadAvatar player={matchWinner} size={64} bodyRadius={21} />
      </div>
      <div className={styles.heroName}>
        {matchTied ? (
          topPlayers.map((p: any, i: number) => [
            i > 0 ? <span key={"sep" + i} className={styles.heroSep}>·</span> : null,
            <span key={p.id} style={{ color: wormColors[p.colorId] }}>{p.name}</span>,
          ])
        ) : (
          <>
            {isFlagCode(matchWinner.flag) ? <img className={styles.podiumFlag + " " + styles.heroFlag} src={flagUrl(matchWinner.flag)} alt="" /> : null}
            <span style={{ color: wormColors[matchWinner.colorId] }}>{matchWinner.name}</span>
          </>
        )}
      </div>
      {/* Just the name and the fact: points and rating live in the bars below. */}
      <div className={styles.heroSub}>{matchTied ? "Match tied" : heroSub || "wins the match"}</div>
    </div>
  );

  const bars = standings.map((s: any) => {
    const color = wormColors[s.player.colorId];
    const winner = s.placement === 1;
    const pct = s.note != null ? 0 : Math.max(0, Math.min(100, (s.score / winnerScore) * 100));
    return (
      <div key={s.player.id} className={styles.barRow + (winner && !s.still ? " " + styles.barRowWinner : "") + (youIds.includes(s.player.id) ? " " + styles.barRowYou : "")}>
        <div className={styles.barFill} style={{ width: `${pct}%`, background: winner ? "#ffd740" : color }} />
        <div className={styles.barEdge} style={{ width: `${pct}%`, borderColor: winner ? "#ffd740" : color, opacity: pct > 0 ? 1 : 0 }} />
        <div className={styles.barBody}>
          <span className={styles.barPlace}>{s.still ? "" : s.placement}</span>
          <span className={styles.barFlag} style={{ "--c": color } as React.CSSProperties}>
            {isFlagCode(s.player.flag) ? <img src={flagUrl(s.player.flag)} alt="" /> : null}
          </span>
          <span className={styles.barName}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{s.player.name}</span>
            {s.left ? <span className={styles.barLeft}>LEFT</span> : null}
          </span>
          {renderRank(s.player.id, "match-over-rank--standing", true)}
          {s.note != null ? <span className={styles.barNote + (s.still ? " " + styles.barStill : "")}>{s.note}</span> : <span className={styles.barTotal}>{s.score}</span>}
        </div>
      </div>
    );
  });

  // Wide maps (the 2:1 rectangle or stadium): wider thumbnails, so they are as
  // readable as the square ones.
  const firstMap = match.matchState.roundsData[0]?.gameState?.map;
  const wideRounds = !!firstMap && firstMap.width > firstMap.height * 1.2;
  const roundElements = match.matchState.roundsData.map((roundData: any, index: number) => {
    const gameState = roundData.gameState;
    const winningPlayerId = scoreUtil.createSortedList(roundData.roundScore)[0].id;
    const winningPlayer = match.matchConfig.players.find((p: any) => p.id === winningPlayerId);

    return (
      <div key={index} className={"round-replay " + styles.matchOverRoundCard}>
        <div
          onClick={() => onRoundClick(index)}
          className="canvas-overlay-hover-wrapper match-over-replay"
          // the box takes the map's own shape (a 2:1 rectangle or stadium is
          // wide), so the board fills it instead of leaving empty room below
          style={{ aspectRatio: `${gameState.map.width} / ${gameState.map.height}` }}
        >
          <GameCanvas gameState={gameState} players={match.matchConfig.players}>
            <Overlay
              gameState={gameState}
              className={"canvas-overlay-text canvas-overlay-hover-effect " + styles.roundReplayHover}
            >
              <div className={styles.roundReplayWatch}>
                <span className={styles.roundReplayPlay}>▶</span>Watch replay
              </div>
            </Overlay>
          </GameCanvas>
        </div>
        <div className={styles.roundReplayCaption}>
          <span className={styles.roundReplayN}>Round {index + 1}</span>
          <span className={styles.roundReplayWinner}>
            <span style={{ color: wormColors[winningPlayer.colorId] }}>{winningPlayer.name}</span>
          </span>
        </div>
      </div>
    );
  });

  return (
    <div className={"match-over-page " + styles.matchOverPage + (celebrationFor(matchWinner) ? " " + styles.matchOverCelebration : "")}>
      <div className={styles.matchOverHeader}>
        {onBack ? (
          <button className={"btn btn-secondary " + styles.matchOverLeave} onClick={onBack}>
            {backLabel}
          </button>
        ) : null}
        <button className={"btn " + (onPlayAgain || onBack ? "btn-secondary " : "btn-primary ") + styles.matchOverLeave} onClick={onExitAction}>
          {exitLabel}
        </button>
        {onPlayAgain ? (
          <button className={"btn btn-primary " + styles.matchOverLeave} onClick={onPlayAgain}>
            {playAgainLabel}
          </button>
        ) : null}
      </div>

      <div className={styles.matchOverSection}>
        <div className={styles.resultsTop}>
          {runnersUp[0] ? flank(runnersUp[0]) : null}
          {hero}
          {runnersUp[1] ? flank(runnersUp[1]) : null}
        </div>
        <div className={styles.resultsBody}>
          <div className={styles.bars}>{bars}</div>
          {(myRatingCard || stats) ? (
            <aside className={styles.resultsSide}>
              {myRatingCard}
              {/* the kebab sits in the side column, between the cards, so the
                  header holds only the two actions */}
              <DonateButton source="match-over" label="Buy us a kebab" icon="🥙" className={"app-donate-cta " + styles.sideDonate} />
              {stats ? <MatchStatsBox stats={stats} players={statsPlayers} initialPlayerId={statsMainId} /> : null}
            </aside>
          ) : null}
        </div>
        <AdSlot unit="results" />
      </div>

      {roundElements.length > 0 ? (
        <div className={styles.matchOverSection}>
          <h3 className={styles.matchOverSectionTitle}>Rounds</h3>
          <div className={styles.matchOverReplays + (wideRounds ? " " + styles.matchOverReplaysWide : "")}>{roundElements}</div>
        </div>
      ) : null}
    </div>
  );
}
